/**
 * The raw `window.Telegram.WebApp` bridge, and the only place its shape is written down.
 *
 * Ported from `legacy/frontend/src/telegram.ts:15-43` at legacy commit 2953e3f.
 *
 * Both SDKs are loaded on purpose — `@telegram-apps/sdk` and the raw
 * `telegram.org/js/telegram-web-app.js` script in `index.html` — and both are read.
 * See ADR-0006 and FND-001 (`KEEP`).
 */

export type HapticFeedbackBridge = {
  notificationOccurred?: (kind: 'success' | 'error' | 'warning') => void;
  impactOccurred?: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
  selectionChanged?: () => void;
};

export type WebAppBridge = {
  initData?: string;
  platform?: string;
  version?: string;
  showScanQrPopup?: (params: { text?: string }, callback: (text: string) => boolean) => void;
  closeScanQrPopup?: () => void;
  ready?: () => void;
  expand?: () => void;
  requestFullscreen?: () => void;
  lockOrientation?: () => void;
  disableVerticalSwipes?: () => void;
  HapticFeedback?: HapticFeedbackBridge;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: WebAppBridge };
  }
}

/**
 * The two clients where a phone is held in a hand. Everything else — desktop, macOS,
 * the web clients — runs in a window somebody sized on purpose, and neither fullscreen
 * nor the orientation lock has anything to do there: fullscreen would blow a 32rem
 * column up to a monitor, and there is nothing to rotate.
 *
 * `legacy/frontend/src/telegram.ts:34-37`.
 */
export const HANDHELD = ['ios', 'android'];

export function webApp(): WebAppBridge | undefined {
  return window.Telegram?.WebApp;
}

/** Which client this is, or `''` outside Telegram. Used as a tag by ADR-0010. */
export function platform(): string {
  return webApp()?.platform ?? '';
}

/** The Bot API version the client reports, or `''`. Used by the gate and as a tag. */
export function clientVersion(): string {
  return webApp()?.version ?? '';
}

export function isHandheld(): boolean {
  return HANDHELD.includes(platform());
}
